import produce from "immer";
import React, { JSX } from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";

import { RootState } from "../../../../store";
import { tl } from "../../../../components/translate";
import HtmlTooltip from "../../../../components/HtmlTooltip";
import { resourceCentreActions as actions } from "../../../../actions";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  GlobalSettings as IGlobalSettings,
  IClientSettings,
  ResourceCentre
} from "../../../../interfaces/ResourceCentreInterface";

export const defaultGlobalSettings: IGlobalSettings = {
  referralSettings: {
    requireReferrer: false,
    requireExtraReferrer: false
  }
};

export const defaultClientSettings: IClientSettings = {
  enablePictureUpload: false,
  enableLeadClients: false,
  saveClientPhotoLocally: false,
  disableWebcamMirroring: false,
  sendAppLinkToClient: false,
  allowClientDuplication: false,
  clientFormSettings: {
    requireEmail: false,
    requireKnownUsFrom: false,
    requirePhoneNumber: false,
    requireDateOfBirth: false,
    requireEthnicity: false
  }
};

// eslint-disable-next-line import/prefer-default-export
export const GlobalSettings = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const resourceCentre: ResourceCentre = useAppSelector(
    (s: RootState) => s.resources.resourceCentres[0] || {}
  );

  const globalSettings = {
    ...defaultGlobalSettings,
    ...resourceCentre?.settings?.globalSettings,
    referralSettings: {
      ...defaultGlobalSettings.referralSettings,
      ...resourceCentre?.settings?.globalSettings?.referralSettings
    }
  };

  const clientSettings = {
    ...defaultClientSettings,
    ...resourceCentre?.settings?.clientSettings,
    clientFormSettings: {
      ...defaultClientSettings.clientFormSettings,
      ...resourceCentre?.settings?.clientSettings?.clientFormSettings
    }
  };

  const updateReferralSetting = (key: keyof IGlobalSettings["referralSettings"]): void => {
    dispatch(
      actions.putResourceCentre(
        produce(resourceCentre, (draft) => {
          if (!draft.settings.globalSettings) {
            draft.settings.globalSettings = defaultGlobalSettings;
          }
          const { referralSettings } = draft.settings.globalSettings;

          referralSettings[key] = !referralSettings[key];
        })
      )
    );
  };

  const updateClientSetting = (key: keyof IClientSettings): void => {
    dispatch(
      actions.putResourceCentre(
        produce(resourceCentre, (draft) => {
          if (!draft.settings.clientSettings) {
            draft.settings.clientSettings = defaultClientSettings;
          }
          const { clientSettings: draftClientSettings } = draft.settings;

          if (key === "allowClientDuplication") {
            draftClientSettings[key] = !draftClientSettings[key];
          }
        })
      )
    );
  };

  const updateClientFormSetting = (key: keyof IClientSettings["clientFormSettings"]): void => {
    dispatch(
      actions.putResourceCentre(
        produce(resourceCentre, (draft) => {
          if (!draft.settings.clientSettings) {
            draft.settings.clientSettings = defaultClientSettings;
          }

          if (!draft.settings.clientSettings.clientFormSettings) {
            draft.settings.clientSettings.clientFormSettings =
              defaultClientSettings.clientFormSettings;
          }

          const { clientFormSettings } = draft.settings.clientSettings;

          clientFormSettings[key] = !clientFormSettings[key];
        })
      )
    );
  };

  const referralSettingsItems = [
    {
      key: "requireReferrer" as const,
      label: tl("requirereferrer"),
      description: "Referrer will be required when creating lab, booking and bills."
    },
    {
      key: "requireExtraReferrer" as const,
      label: tl("requireExtraReferrer"),
      description: "Extra referrer will be required when creating lab, booking and bills."
    }
  ];

  const clientSettingsItems = [
    {
      key: "allowClientDuplication" as const,
      label: tl("AllowClientDuplication"),
      description: "Allow multiple clients with same phone number."
    }
  ];

  const clientFormSettingsItems = [
    {
      key: "requireEmail" as const,
      label: tl("requireEmail"),
      description: "Email will be required when creating client."
    },
    {
      key: "requireKnownUsFrom" as const,
      label: tl("requireKnownUsFrom"),
      description: "Known us from will be required when creating client."
    },
    {
      key: "requirePhoneNumber" as const,
      label: tl("requirePhNo."),
      description: "Phone number will be required when creating client."
    },
    {
      key: "requireDateOfBirth" as const,
      label: tl("requireDateOfBirth"),
      description: "Date of birth will be required when creating client."
    },
    {
      key: "requireEthnicity" as const,
      label: tl("requireEthnicity"),
      description: "Client ethincity will be required when creating client."
    }
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Common Referral Settings
        </Typography>

        {referralSettingsItems.map((item) => (
          <Box key={item.key} sx={{ pl: 2, display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={globalSettings.referralSettings[item.key]}
                  onChange={() => updateReferralSetting(item.key)}
                />
              }
              label={item.label}
            />

            <HtmlTooltip description={item.description} />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Common Client Settings
        </Typography>

        {clientSettingsItems.map((item) => (
          <Box key={item.key} sx={{ pl: 2, display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={clientSettings[item.key] as boolean}
                  onChange={() => updateClientSetting(item.key)}
                />
              }
              label={item.label}
            />

            <HtmlTooltip description={item.description} />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Client Form Settings
        </Typography>

        {clientFormSettingsItems.map((item) => (
          <Box key={item.key} sx={{ pl: 2, display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={clientSettings.clientFormSettings[item.key]}
                  onChange={() => updateClientFormSetting(item.key)}
                />
              }
              label={item.label}
            />

            <HtmlTooltip description={item.description} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};
